// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { FormFieldStyle } from "@insite/mobius/FormField";
import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
    Pagination,
} from "@insite/mobius/index";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { PaginationStyle } from "@insite/mobius/Pagination";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@rc/App/Components/Notify/Notify";
import loadAddressBookBillTos from "@rc/App/Handlers/Components/AddressBook/LoadAddressBookBillTos";
import loadAddressBookShipTos from "@rc/App/Handlers/Components/AddressBook/LoadAddressBookShipTos";
import TextField from "@rc/MobiusOverrides/TextField";
import { maxLengthAttr, xMask } from "@rc/XDL/Utilities";
import debounce from "lodash/debounce";
import uniqueId from "lodash/uniqueId";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isShipTo: boolean;
    billToId?: any;
    onClose?: () => void;
    onRowSelect?: (item: any) => void;
}

const mapDispatchToProps = {
    loadAddressBookBillTos,
    loadAddressBookShipTos,
};

type Props = ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface AddressBookStyles {
    modalStyles?: ModalPresentationProps;
    wrapper?: InjectableCss;
    paginationWrapper?: InjectableCss;
}

export const addressBookStyles: AddressBookStyles = {
    modalStyles: {
        mergeCss: true,
        cssOverrides: {
            modalTitle: css`
                max-width: 1300px;
                margin: 0 auto;
                width: 100%;
                position: relative;
            `,
            modalContent: css`
                max-width: 1300px;
                margin: 0 auto;
                width: 100%;
            `,
        },
    },
    wrapper: {
        css: css`
            margin-bottom: 18px;
            tbody tr {
                cursor: pointer;

                &:first-child td {
                    padding-top: 11px;
                    input {
                        min-width: 180px;
                    }
                }
            }
        `,
    },
    paginationWrapper: {
        css: css`
            ${PaginationStyle} [class^="PerPageSelect"] ${FormFieldStyle} {
                width: 198px;
            }
        `,
    },
};

const AddressBook: FC<Props> = ({
    isShipTo,
    billToId,
    onClose,
    onRowSelect,
    loadAddressBookBillTos,
    loadAddressBookShipTos,
}) => {
    const styles = addressBookStyles;

    const [customerNumber, setCustomerNumber] = useState();
    const [companyName, setCompanyName] = useState();
    const [state, setState] = useState();
    const [postalCode, setPostalCode] = useState();
    const [phone, setPhone] = useState<string>();

    const [isFormEmpty, setIsFormEmpty] = useState(true);
    const [isLoading, setIsLoading] = useState(true);

    const [data, setData] = useState({} as any);
    const [dataParameter, setDataParameter] = useState<any>({
        expand: isShipTo ? ["validation", "excludeShowAll", "excludeOneTime"] : ["state"],
        filter: "",
        page: 1,
        pageSize: 9,
        billToId,
    });

    useEffect(() => {
        setIsLoading(true);
        isShipTo
            ? loadAddressBookShipTos({
                  ...dataParameter,
                  onSuccess: (data: any) => {
                      setIsLoading(false);
                      setData(data);
                  },
              })
            : loadAddressBookBillTos({
                  ...dataParameter,
                  onSuccess: (data: any) => {
                      setIsLoading(false);
                      setData(data);
                  },
              });
    }, [dataParameter]);

    const changePage = (newPageNumber: any) => {
        setDataParameter({
            ...dataParameter,
            page: newPageNumber,
        });
    };

    const changeNumberOfResultsPerPage = (event: any) => {
        // eslint-disable-next-line radix
        const resultsPerPage = parseInt(event.target.value);

        setDataParameter({
            ...dataParameter,
            page: 1,
            pageSize: resultsPerPage,
        });
    };

    const doSearch = (event: any) => {
        debouncedDoSearch(event.currentTarget.value);
    };

    const debouncedDoSearch = debounce((value: string) => {
        const unMaskedPhone = phone?.replace(/\D/g, "");
        const addressBookForm = {
            customerNumber,
            companyName,
            state,
            postalCode,
            phone: unMaskedPhone,
        } as any;

        const form = {} as any;

        let isEmpty = true;
        Object.keys(addressBookForm).forEach(key => {
            if (addressBookForm[key]) {
                setIsFormEmpty(false);
                isEmpty = false;
                form[key] = addressBookForm[key];
            }
        });

        if (isEmpty) {
            setIsFormEmpty(true);
            isEmpty = true;
        }
        const filters = Object.keys(form).length !== 0 && (JSON.stringify(form) || "");

        setDataParameter({
            ...dataParameter,
            page: 1,
            filter: encodeURIComponent(filters || ""),
        });
    }, 800);

    const renderTableRaws = () => {
        if (isShipTo) {
            return (
                <>
                    {data?.shipTos?.length > 0 ? (
                        data?.shipTos?.map((shipTo: any) => (
                            <>
                                {!shipTo.isNew && (
                                    <DataTableRow
                                        key={uniqueId()}
                                        onClick={() => {
                                            onRowSelect?.(shipTo);
                                        }}
                                    >
                                        <DataTableCell>
                                            {isShipTo ? shipTo.customerSequence : shipTo.customerNumber}
                                        </DataTableCell>
                                        <DataTableCell>{shipTo.companyName}</DataTableCell>
                                        <DataTableCell>{shipTo.state && shipTo.state.name}</DataTableCell>
                                        <DataTableCell>{shipTo.postalCode}</DataTableCell>
                                        <DataTableCell>{shipTo.phone}</DataTableCell>
                                    </DataTableRow>
                                )}
                            </>
                        ))
                    ) : (
                        <>
                            {!isLoading && (
                                <DataTableRow key={Math.random() * 10}>
                                    <DataTableCell>{translate("No data to display")}</DataTableCell>
                                </DataTableRow>
                            )}
                        </>
                    )}
                </>
            );
        }
        return (
            <>
                {data?.billTos?.length > 0 ? (
                    data?.billTos?.map((billTo: any) => (
                        <>
                            {!billTo.isNew && (
                                <DataTableRow
                                    key={uniqueId()}
                                    onClick={() => {
                                        onRowSelect?.(billTo);
                                    }}
                                >
                                    <DataTableCell>
                                        {isShipTo ? billTo.customerSequence : billTo.customerNumber}
                                    </DataTableCell>
                                    <DataTableCell>{billTo.companyName}</DataTableCell>
                                    <DataTableCell>{billTo.state && billTo.state.name}</DataTableCell>
                                    <DataTableCell>{billTo.postalCode}</DataTableCell>
                                    <DataTableCell>{billTo.phone}</DataTableCell>
                                </DataTableRow>
                            )}
                        </>
                    ))
                ) : (
                    <>
                        {!isLoading && (
                            <DataTableRow key={Math.random() * 10}>
                                <DataTableCell>{translate("No data to display")}</DataTableCell>
                            </DataTableRow>
                        )}
                    </>
                )}
            </>
        );
    };

    return (
        <>
            <Modal
                sizeVariant="large"
                isOpen={true}
                handleClose={onClose}
                headline={translate("Filter & Select Address")}
                {...styles.modalStyles}
            >
                <DataTable {...styles.wrapper}>
                    <DataTableHead>
                        <DataTableHeader>
                            {isShipTo ? translate("Customer Ship-To #") : translate("Customer #")}
                        </DataTableHeader>
                        <DataTableHeader>{translate("Company Name")}</DataTableHeader>
                        <DataTableHeader>{translate("State")}</DataTableHeader>
                        <DataTableHeader>{translate("Postal Code")}</DataTableHeader>
                        <DataTableHeader>{translate("Phone Number")}</DataTableHeader>
                    </DataTableHead>
                    <DataTableBody>
                        <DataTableRow>
                            <DataTableCell>
                                <TextField
                                    label={
                                        isShipTo
                                            ? translate("Search Customer Ship-To #")
                                            : translate("Search Customer #")
                                    }
                                    value={customerNumber}
                                    onChange={(e: any) => {
                                        setCustomerNumber(e.target.value);
                                    }}
                                    onKeyUp={doSearch}
                                />
                            </DataTableCell>
                            <DataTableCell>
                                <TextField
                                    label={translate("Search Company Name")}
                                    value={companyName}
                                    onChange={(e: any) => {
                                        setCompanyName(e.target.value);
                                    }}
                                    onKeyUp={doSearch}
                                    maxLength={40}
                                />
                            </DataTableCell>
                            <DataTableCell>
                                <TextField
                                    label={translate("Search State")}
                                    value={state}
                                    onChange={(e: any) => {
                                        setState(e.target.value);
                                    }}
                                    onKeyUp={doSearch}
                                />
                            </DataTableCell>
                            <DataTableCell>
                                <TextField
                                    label={translate("Search Postal Code")}
                                    value={postalCode}
                                    onChange={(e: any) => {
                                        setPostalCode(e.target.value);
                                    }}
                                    onKeyUp={doSearch}
                                    maxLength={10}
                                />
                            </DataTableCell>
                            <DataTableCell>
                                <TextField
                                    label={translate("Search Phone Number")}
                                    value={phone}
                                    onChange={(e: any) => {
                                        setPhone(xMask.phoneNumberUS(e.target.value));
                                    }}
                                    onKeyUp={doSearch}
                                    maxLength={maxLengthAttr.phone}
                                />
                            </DataTableCell>
                        </DataTableRow>
                        {renderTableRaws()}
                    </DataTableBody>
                </DataTable>
                <StyledWrapper {...styles.paginationWrapper}>
                    {data?.billTos?.length > 0 && (
                        <Pagination
                            resultsPerPage={data?.pagination?.pageSize}
                            resultsCount={data?.pagination?.totalItemCount}
                            currentPage={data?.pagination?.currentPage || "1"}
                            resultsPerPageOptions={data?.pagination?.pageSizeOptions}
                            onChangeResultsPerPage={changeNumberOfResultsPerPage}
                            onChangePage={changePage}
                        />
                    )}
                </StyledWrapper>
            </Modal>
        </>
    );
};

export default connect(null, mapDispatchToProps)(AddressBook);
